import React, { useEffect, useState } from 'react'
import { getOptionsConfig } from '@rawgraphs/rawgraphs-core'
import ExportPreview from '../ExportPreview/ExportPreview'
import Exporter from '../Exporter'
import Loading from '../DataLoader/loading'
import { buildRawVizForExport } from '../../services/exportRawViz'
import PreviewSizeControl from './PreviewSizeControl'
import WorkflowMobileSheet from './WorkflowMobileSheet'
import { useDataContext } from '../../contexts/DataContext'
import { useChartContext } from '../../contexts/ChartContext'
import { useUIContext } from '../../contexts/UIContext'

export default function ExportStep({ actions }) {
  const { filteredData: data } = useDataContext()

  const {
    currentChart,
    mapping,
    visualOptions,
    brandTheme,
    chartColorSyncMode,
    chartColorOptionMeta,
    exportLugsailProject,
  } = useChartContext()

  const {
    previewDimensions,
    previewSizePreset,
    setPreviewSizePreset,
    previewCustomSize,
    setPreviewCustomSize,
    previewSizePresets,
  } = useUIContext()

  const dataTypes = data?.dataTypes
  const recolorMarks = !getOptionsConfig(currentChart?.visualOptions)?.colorScale

  const [exportRawViz, setExportRawViz] = useState(null)
  const [exportLoading, setExportLoading] = useState(false)

  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  )

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const handleChange = () => setIsMobileLayout(mediaQuery.matches)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  useEffect(() => {
    let isCancelled = false

    async function buildExportViz() {
      setExportLoading(true)
      try {
        const nextRawViz = await buildRawVizForExport({
          chart: currentChart,
          dataset: data?.dataset,
          dataTypes,
          mapping,
          visualOptions,
        })
        if (!isCancelled) {
          setExportRawViz(nextRawViz)
        }
      } catch (error) {
        if (!isCancelled) {
          setExportRawViz(null)
        }
      } finally {
        if (!isCancelled) {
          setExportLoading(false)
        }
      }
    }

    buildExportViz()
    return () => {
      isCancelled = true
    }
  }, [currentChart, data, dataTypes, mapping, visualOptions])

  const exportPreview = exportLoading ? (
    <div className="chart-preview">
      <div className="chart-preview-stage">
        <Loading />
      </div>
    </div>
  ) : (
    <ExportPreview
      rawViz={exportRawViz}
      brandTheme={brandTheme}
      chartId={currentChart?.metadata?.id}
      mapping={mapping}
      dataTypes={dataTypes}
      visualOptions={visualOptions}
      chartColorSyncMode={chartColorSyncMode}
      recolorMarks={recolorMarks}
    />
  )

  const exporterPanel = (
    <Exporter
      rawViz={exportRawViz}
      exportLugsailProject={exportLugsailProject}
      brandTheme={brandTheme}
      currentChart={currentChart}
      dataset={data?.dataset}
      mapping={mapping}
      dataTypes={dataTypes}
      visualOptions={visualOptions}
      chartColorSyncMode={chartColorSyncMode}
      hasMappedColorField={chartColorOptionMeta.hasMappedColorField}
    />
  )

  if (isMobileLayout) {
    return (
      <WorkflowMobileSheet
        mainContent={exportPreview}
        topStrip={
          <PreviewSizeControl
            previewSizePreset={previewSizePreset}
            setPreviewSizePreset={setPreviewSizePreset}
            previewCustomSize={previewCustomSize}
            setPreviewCustomSize={setPreviewCustomSize}
            previewDimensions={previewDimensions}
            presets={previewSizePresets}
          />
        }
        sheetContent={exporterPanel}
        actions={actions}
      />
    )
  }

  return (
    <div className="workflow-stage">
      <div className="workflow-step-header">
        <div className="workflow-step-copy">
          <h2>Export</h2>
          <p>
            Download your chart as SVG, PNG, JPG, or a reusable project
            file.
          </p>
          {data?.dataset?.length ? (
            <div className="small text-muted mt-2">
              Export uses the full dataset: {data.dataset.length.toLocaleString()} rows.
            </div>
          ) : null}
        </div>
        <div className="workflow-step-actions-desktop">{actions}</div>
      </div>
      <div className="workflow-split workflow-split--export">
        <div className="workflow-panel workflow-panel-left">
          {exporterPanel}
        </div>
        <div className="workflow-panel workflow-panel-preview">
          {exportPreview}
          <PreviewSizeControl
            previewSizePreset={previewSizePreset}
            setPreviewSizePreset={setPreviewSizePreset}
            previewCustomSize={previewCustomSize}
            setPreviewCustomSize={setPreviewCustomSize}
            previewDimensions={previewDimensions}
            presets={previewSizePresets}
          />
        </div>
      </div>
      <div className="workflow-step-actions-mobile">{actions}</div>
    </div>
  )
}
